import { getAfterCompletionRedirectPath } from '@/helpers/getAfterCompletionRedirectPath';
import { resolveMilestonePackageRoute } from '@/helpers/milestonePackageRoute';
import usePermissions from '@/hooks/usePermissions';
import { useGetPerformProgressQuery } from '@/services/organizations';
import { Badge, BadgeVariant, Box, Button, Card, useBreakpoint } from '@palmetto/palmetto-components';
import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AccountMilestone,
  AccountRequirementDocument,
  AccountRequirementStatus,
  MilestonePackageRoute,
  MilestoneRequirement,
  MilestoneType,
  Packages,
  QueueType,
  UserPermissions,
} from 'types';

import { useGetAccountQuery } from '../../services/accounts';
import { useGetQueueByAccountIdAndTypeQuery } from '../../services/queues';
import { useIsOrgReadOnly } from '../auth/claimsUtils';

const statusMap: {
  [key in AccountRequirementStatus]: {
    label: string;
    variant: BadgeVariant;
  };
} = {
  [AccountRequirementStatus.completed]: { label: 'Approved', variant: 'success' },
  [AccountRequirementStatus.rejected]: { label: 'Rejected', variant: 'danger' },
  [AccountRequirementStatus.pending]: { label: 'Pending', variant: 'warning' },
  [AccountRequirementStatus.submitted]: { label: 'Submitted', variant: 'warning' },
  [AccountRequirementStatus.resubmitted]: { label: 'Resubmitted', variant: 'warning' },
  [AccountRequirementStatus.inProgress]: { label: 'Pending', variant: 'warning' },
  [AccountRequirementStatus.error]: { label: 'Error', variant: 'warning' },
} as const;

export const LoopFieldMilestonePackageContainer = () => {
  const { id } = useParams<{ id: any }>();
  const { data: account } = useGetAccountQuery(id);
  const navigate = useNavigate();
  const { isPhone } = useBreakpoint();
  const { data: designDocsQueue } = useGetQueueByAccountIdAndTypeQuery({
    accountId: id,
    type: QueueType.loopFieldDesignDocuments,
  });
  const userPermissions = usePermissions();
  const isLightReachQCTech = userPermissions.includes(UserPermissions.lightReachQCTech);
  const allowsAction = useIsOrgReadOnly();
  const { data: canPerformProgressBasedOnOrgStatus } = useGetPerformProgressQuery(id);

  const loopFieldRequirement = useMemo(() => {
    if (!account) return;
    const loopFieldMilestone = account.milestones?.find((m: AccountMilestone) => m.type === MilestoneType.loopField);
    return loopFieldMilestone?.requirements.find(
      (req: AccountRequirementDocument) => req.type === MilestoneRequirement.loopFieldPackage,
    );
  }, [account]);

  const status: AccountRequirementStatus = loopFieldRequirement
    ? loopFieldRequirement.status
    : AccountRequirementStatus.pending;
  const isCreated = status !== AccountRequirementStatus.pending;

  const handleClick = useCallback(() => {
    const route = resolveMilestonePackageRoute({
      accountId: id,
      programType: account?.programType,
      packageType: Packages.loopFieldPackage,
      hasDesignDocsQueue: Boolean(designDocsQueue),
    });
    navigate(route.path, route.state ? { state: route.state } : undefined);
  }, [navigate, id, account, designDocsQueue]);

  const handleEditClick = useCallback(() => {
    navigate(`/accounts/${id}/loop-field-package/edit`);
  }, [navigate, id]);

  const handleViewClick = useCallback(() => {
    const routePath = getAfterCompletionRedirectPath(id, MilestonePackageRoute.loopFieldPackage, isLightReachQCTech);
    navigate(routePath);
  }, [navigate, id, isLightReachQCTech]);

  return (
    <Card margin="0 0 lg 0">
      <Box childGap="lg" padding="lg" alignItems="center" direction="row" justifyContent="space-between">
        <Box as="h3" fontWeight="medium" fontSize="md" disabled={!allowsAction}>
          Loop Field Package
        </Box>
        {!isCreated && (
          <Button
            onClick={handleClick}
            disabled={!canPerformProgressBasedOnOrgStatus || !allowsAction}
            size="sm"
            variant="primary"
            tone="neutral"
            iconPrefix="add"
          >
            {!isPhone && 'Loop Field Package'}
          </Button>
        )}
      </Box>
      <Box
        borderWidth="xs 0 0 0"
        borderColor="separator"
        background="primary"
        padding={{ base: 'md 0', tablet: '0' }}
        direction="row"
        width="100"
        alignItems="center"
      >
        {isCreated ? (
          <>
            <Box width={{ base: '100', tablet: '60' }} direction={{ base: 'column', tablet: 'row' }}>
              <Box
                alignItems="flex-start"
                width={{ base: '100', tablet: '30' }}
                padding={{ base: 'md lg', tablet: 'md md md lg' }}
              >
                <Badge message={statusMap[status].label} variant={statusMap[status].variant} />
              </Box>
              <Box
                width={{ base: '100', tablet: '70' }}
                display="block"
                childGap="xs"
                padding={{ base: '0 lg', tablet: 'md md md lg' }}
              >
                <Box fontWeight="medium">Loop Field Package</Box>
              </Box>
            </Box>
            <Box
              width="40"
              padding={{ base: '0 lg', tablet: 'md lg' }}
              direction={{ base: 'column', tablet: 'row' }}
              childGap="xs"
              justifyContent="flex-end"
              alignItems="flex-end"
            >
              {status !== AccountRequirementStatus.completed && (
                <Button
                  size="sm"
                  variant="secondary"
                  tone="neutral"
                  onClick={handleEditClick}
                  disabled={!allowsAction}
                >
                  Edit
                </Button>
              )}
              {status === AccountRequirementStatus.completed && (
                <Button size="sm" variant="secondary" tone="neutral" onClick={handleViewClick}>
                  View
                </Button>
              )}
            </Box>
          </>
        ) : null}
      </Box>
    </Card>
  );
};
